import React, { ChangeEvent, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile,
  ContentCopy,
  Delete,
  Edit,
  Link as LinkIcon,
  OpenInNew,
  UploadFile,
} from '@mui/icons-material';
import { collection, deleteDoc, doc, orderBy, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { useFirestoreQuery } from '../hooks/useFirestoreQuery';
import { db, storage } from '../lib/firebase';
import { AttachmentFile } from '../types/attachment';
import { buildPublicAttachmentUrl } from './PublicAttachmentPage';

const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const buildStoragePath = (documentId: string, fileName: string) => {
  const safeName = fileName.replace(/[^\w.\-가-힣]/g, '_');
  return `attachments/${documentId}/${Date.now()}_${safeName}`;
};

export const AttachmentPage: React.FC = () => {
  const { user } = useAuth();
  const { data: attachments, loading, error } = useFirestoreQuery<AttachmentFile>(
    'attachments',
    orderBy('createdAt', 'desc')
  );

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [editingFile, setEditingFile] = useState<AttachmentFile | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [deletingFile, setDeletingFile] = useState<AttachmentFile | null>(null);

  const totalSize = useMemo(
    () => attachments.reduce((sum, item) => sum + (item.size || 0), 0),
    [attachments]
  );

  const resetUploadForm = () => {
    setSelectedFile(null);
    setDisplayName('');
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setDisplayName(file?.name ?? '');
    setMessage(null);
    setPageError(null);
    event.target.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setPageError('표시명을 입력해주세요.');
      return;
    }

    setUploading(true);
    setMessage(null);
    setPageError(null);

    try {
      const docRef = doc(collection(db, 'attachments'));
      const storagePath = buildStoragePath(docRef.id, selectedFile.name);
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, selectedFile, {
        contentType: selectedFile.type || 'application/octet-stream',
        customMetadata: {
          attachmentId: docRef.id,
          uploadedBy: user.uid,
        },
      });
      const downloadUrl = await getDownloadURL(fileRef);

      await setDoc(docRef, {
        displayName: trimmedName,
        originalName: selectedFile.name,
        contentType: selectedFile.type || 'application/octet-stream',
        size: selectedFile.size,
        storagePath,
        downloadUrl,
        publicUrl: buildPublicAttachmentUrl(docRef.id, storagePath),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user.uid,
      });

      resetUploadForm();
      setMessage('첨부파일이 업로드되었습니다.');
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setPageError('첨부파일 업로드에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setUploading(false);
    }
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setMessage('URL 링크주소를 복사했습니다.');
      setPageError(null);
    } catch (err) {
      console.error('Error copying attachment URL:', err);
      setPageError('URL 복사에 실패했습니다.');
    }
  };

  const openEditDialog = (file: AttachmentFile) => {
    setEditingFile(file);
    setEditDisplayName(file.displayName);
  };

  const handleUpdate = async () => {
    if (!editingFile) return;

    const trimmedName = editDisplayName.trim();
    if (!trimmedName) {
      setPageError('표시명을 입력해주세요.');
      return;
    }

    try {
      await updateDoc(doc(db, 'attachments', editingFile.id), {
        displayName: trimmedName,
        updatedAt: serverTimestamp(),
      });
      setEditingFile(null);
      setMessage('첨부파일 정보가 수정되었습니다.');
      setPageError(null);
    } catch (err) {
      console.error('Error updating attachment:', err);
      setPageError('첨부파일 정보 수정에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!deletingFile) return;

    try {
      if (deletingFile.storagePath) {
        await deleteObject(ref(storage, deletingFile.storagePath));
      }
      await deleteDoc(doc(db, 'attachments', deletingFile.id));
      setDeletingFile(null);
      setMessage('첨부파일이 삭제되었습니다.');
      setPageError(null);
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setPageError('첨부파일 삭제에 실패했습니다.');
    }
  };

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFile sx={{ fontSize: 32, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                첨부파일 관리
              </Typography>
              <Typography variant="body2" color="text.secondary">
                외부에 공유할 첨부파일과 URL 링크주소를 관리합니다.
              </Typography>
            </Box>
          </Box>
        </Box>

        {(message || pageError || error) && (
          <Box sx={{ mb: 2 }}>
            {message && <Alert severity="success">{message}</Alert>}
            {(pageError || error) && (
              <Alert severity="error" sx={{ mt: message ? 1 : 0 }}>
                {pageError || '첨부파일 목록을 불러오는데 실패했습니다.'}
              </Alert>
            )}
          </Box>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            신규 첨부파일 등록
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr auto' }, gap: 2, alignItems: 'center' }}>
            <Button variant="outlined" component="label" startIcon={<UploadFile />} disabled={uploading}>
              파일 선택
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
            <TextField
              label="표시명"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              disabled={uploading}
              placeholder="링크 관리 화면에 표시할 이름"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFile />}
              sx={{ minWidth: 120 }}
            >
              업로드
            </Button>
          </Box>
          {selectedFile && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              선택한 파일: {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </Typography>
          )}
        </Paper>

        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={700}>
              업로드된 첨부파일 {attachments.length}개
            </Typography>
            <Typography variant="body2" color="text.secondary">
              총 용량 {formatFileSize(totalSize)}
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, width: '28%' }}>표시명</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '18%' }}>원본 파일명</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '12%' }}>용량</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '17%' }}>등록일</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '25%' }}>URL 링크주소</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : attachments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        등록된 첨부파일이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  attachments.map((file) => (
                    <TableRow key={file.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {file.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {file.contentType || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {file.originalName}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatFileSize(file.size || 0)}</TableCell>
                      <TableCell>
                        {file.createdAt
                          ? format(file.createdAt.toDate(), 'yyyy-MM-dd HH:mm', { locale: ko })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LinkIcon fontSize="small" color="action" />
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={buildPublicAttachmentUrl(file.id, file.storagePath)}
                          >
                            {buildPublicAttachmentUrl(file.id, file.storagePath)}
                          </Typography>
                          <Tooltip title="링크 복사">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyLink(buildPublicAttachmentUrl(file.id, file.storagePath))}
                                disabled={!file.downloadUrl}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="새 창에서 열기">
                            <span>
                              <IconButton
                                size="small"
                                component="a"
                                href={buildPublicAttachmentUrl(file.id, file.storagePath)}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={!file.downloadUrl}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="표시명 수정">
                            <IconButton size="small" onClick={() => openEditDialog(file)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="삭제">
                            <IconButton size="small" color="error" onClick={() => setDeletingFile(file)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog open={!!editingFile} onClose={() => setEditingFile(null)} fullWidth maxWidth="sm">
        <DialogTitle>첨부파일 정보 수정</DialogTitle>
        <DialogContent>
          <TextField
            label="표시명"
            value={editDisplayName}
            onChange={(event) => setEditDisplayName(event.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingFile(null)}>취소</Button>
          <Button variant="contained" onClick={handleUpdate}>수정</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deletingFile} onClose={() => setDeletingFile(null)} fullWidth maxWidth="sm">
        <DialogTitle>첨부파일 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            {deletingFile?.displayName} 파일을 삭제하시겠습니까?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            삭제된 파일과 URL 링크주소는 복구할 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingFile(null)}>취소</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>삭제</Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};
